import axiosInstance from './axiosInstance';

/**
 * Importing a workshop's history out of another product.
 *
 * 🔑 Two parameters this used to take and deliberately no longer does:
 *
 * - **`kind`** — the server reads the file's own header. The workbook has two customer
 *   tabs whose CSVs both start with `Customer ID`, so choosing wrong did not fail: the
 *   Contacts file read as a Customers export imports every contact with no phone and
 *   no email.
 * - **`commit`** — an import writes. What makes one press safe is that re-running is
 *   harmless: every path keys on the source system's own id, so a second run matches
 *   what the first created instead of duplicating it.
 *
 * 🔑 Uploading QUEUES; nothing runs until {@link startImportsApi} is called for the
 * session. That is what lets nine files go up one request at a time — the server
 * accepts 10 MB per request and the whole export is several times that — and still
 * run in order, one after another, with nobody's browser holding a connection open.
 *
 * @param file      one .csv export, already split into a part small enough to accept
 * @param sessionId groups every part of one press of Start into a single migration
 * @param sequence  this part's place in that migration, 1-based, across ALL slots —
 *                  the order the server runs them in
 */
export const importMachanicdeskApi = async (file, { sessionId, sequence } = {}) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axiosInstance.post('/imports/machanicdesk', formData, {
        params: {
            ...(sessionId ? { sessionId } : {}),
            ...(sequence != null ? { sequence } : {}),
        },
        // ⚠️ LOAD-BEARING, not decoration. The instance default is application/json,
        // and axios's transformRequest reads:
        //
        //     if (isFormData) return hasJSONContentType ? JSON.stringify(formDataToJSON(data)) : data;
        //
        // so with JSON standing, the FormData carrying the file is FLATTENED TO JSON
        // and posted as JSON — the server answers 415 Unsupported Media Type. Naming
        // multipart here makes hasJSONContentType false; the xhr adapter then replaces
        // this value with the browser's own, boundary and all. Every upload helper in
        // this directory does the same, and omitting it is how this one 415'd.
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
};

/**
 * Run everything queued under a session, in sequence order, on the server.
 *
 * ⚠️ A JSON body, not a query parameter — a body-less POST with a @QueryValue 415s
 * every axios call and logs nothing (see the backend's CLAUDE.md).
 *
 * @returns `{ started }` — how many queued runs the server took on
 */
export const startImportsApi = async (sessionId) => {
    const response = await axiosInstance.post('/imports/machanicdesk/start', { sessionId });
    return response.data;
};

/**
 * How many runs a page holds.
 *
 * Ten, not twenty: a split migration is ten parts of one file, so a page now holds one
 * upload's worth and the run before it starts a new page. The server caps `size` at 100
 * and rejects more, so this can be raised but not past that.
 */
export const IMPORTS_PER_PAGE = 10;

/**
 * One page of this workshop's imports, newest first.
 *
 * 🔑 Paged because each run carries its whole report — every skipped row with its raw
 * cell values — so a workshop mid-migration has megabytes here and this used to fetch
 * all of it to draw a list of one-line summaries.
 *
 * @returns `{ items, page, size, total, totalPages }`
 */
export const getImportRunsApi = async (page = 0, size = IMPORTS_PER_PAGE) => {
    const response = await axiosInstance.get('/imports', { params: { page, size } });
    return response.data;
};

/** One run in full, including the rows it skipped and what it had to guess. */
export const getImportRunApi = async (uuid) => {
    const response = await axiosInstance.get(`/imports/${uuid}`);
    return response.data;
};
